import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type MutableRefObject,
} from 'react'
import { Box } from '@mui/material'
import { useSelector } from 'react-redux'
import toast from 'react-hot-toast'
import { getApiErrorMessage, isRetryableApiError } from '@/helpers/notify'
import { retireCreatedDrafts } from '@/utils/stagedSave'
import { AreaType } from '@/types/areaZone'
import useTranslation from '@/hooks/useTranslation'
import { usePermission } from '@/hooks/permissions'
import areaApi from '@/api/config/areaApi'
import { CONFIG_NAME_MAX_LENGTH } from '@/constant/common'
import { selectTheme } from '@/store/selectors/ThemeSelector'
import { mode_type } from '@/theme/types/constants'
import DeleteIconButton from '@/features/config/components/common/DeleteIconButton'
import DataGrid, {
  gridCellClasses,
  gridRowClasses,
} from '@/components/grid/DataGrid'
import useGuidedAutosave from '@/features/config/hooks/useGuidedAutosave'
import useSerialisedSave from '@/features/config/hooks/useSerialisedSave'
import { useIsGuidedSetup } from '@/features/config/contexts/guided-edit-context'
import type { GridApi, GridCellCtx, GridColumn } from '@/components/grid/types'
import {
  commitOrHoldOpenEditor,
  type GuidedSaveOptions,
} from '@/components/grid/commitOpenEditor'
import {
  discardDraftWithChildren,
  isTopLevelParentId,
  resolveCreatedParents,
  type CreatedParent,
} from './strandedSubAreas'

// Area writes must be serialised: the API returns 500 under concurrent writes.
const SAVE_RETRY_LIMIT = 3
const SAVE_RETRY_DELAY_MS = 300
const SAVE_REQUEST_GAP_MS = 120

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

interface AreasAndZonesTableGridProps {
  rows: AreaType[]
  interfaceId: number
  /** First load only — draws skeleton rows in place of an empty grid. */
  loading?: boolean
  onEditItem: (
    id: number,
    parentId: string,
    name: string,
    options?: { isParent?: boolean },
  ) => void
  onAddSubItem: (id: number, parentName: string) => void
  onDeleteItem: (
    id: number,
    interfaceId: string,
    mode: string,
    name: string,
  ) => void
  bulkEditMode?: boolean
  onRefetch?: () => void
  onSaveChanges?: () => void
  actionsRef?: MutableRefObject<AreasGridActions | null>
}

export interface AreasGridActions {
  save: () => Promise<{ failed: number; total: number }>
  cancel: () => void
}

/**
 * `saved` rows came from the server. `addAreaChip` / `addZoneChip` are the
 * permanent "+ Add …" affordances — they hold no data and are never written.
 * `draft` rows are staged creates, committed by the toolbar's Save alongside
 * the renames, so adding an item behaves the same way as editing one.
 */
type RowKind = 'saved' | 'draft' | 'addAreaChip' | 'addZoneChip'

interface TreeDataRow {
  id: number
  interfaceId: number
  name: string
  parentId: string
  level: number
  isParent: boolean
  parentName?: string
  path: string[]
  kind: RowKind
}

/** A create staged in the grid; nothing is POSTed until the toolbar Save runs. */
interface DraftRow {
  id: number
  interfaceId: number
  name: string
  /** '' for a new area; the owning area's id for a new sub-area. */
  parentId: string
  isParent: boolean
}

const CREATE_AREA_ID = -2
const CREATE_AREA_PLACEHOLDER = '__CREATE_AREA__'
const CREATE_ZONE_PLACEHOLDER = '__CREATE_ROW__'

// The "+ Add Sub Area" chip of an area, derived from the area id so it stays
// stable across refetches (the tree keys expansion off it).
const addZoneChipId = (areaId: number) => -(areaId + 100)

/**
 * Draft ids come from a counter seeded here and decremented per draft, so any
 * number of drafts can be staged at once without colliding — with each other,
 * or with the chip ids (-2 and -(areaId + 100)), which sit far above this band.
 * Negative throughout: the rest of the file, and StationTableView, already read
 * a negative id as "not on the server yet".
 */
const DRAFT_ID_START = -1_000_000

/**
 * True when a row's `parentId` names an area that is itself still a draft.
 * Draft ids run below DRAFT_ID_START, well clear of the fixed chip ids.
 */
const isDraftParentId = (parentId: string) =>
  parentId !== '' && Number(parentId) <= DRAFT_ID_START

const ROW_ACTIONS_SX = {
  display: 'flex',
  height: '100%',
  alignItems: 'center',
  justifyContent: 'center',
}

/** The dashed "+ Add Area" / "+ Add Sub Area" affordance on a create row. */
const AddRowChip: FC<{
  label: string
  disabled: boolean
  isDark: boolean
  onClick: () => void
}> = ({ label, disabled, isDark, onClick }) => (
  <Box
    component="span"
    data-no-cell-edit
    onClick={disabled ? undefined : onClick}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '2px 12px',
      height: '36px',
      borderRadius: '8px',
      border: disabled
        ? '1px dashed #e0e0e0'
        : `1px dashed ${isDark ? 'rgba(52, 211, 153, 0.5)' : 'rgba(0, 71, 68, 0.5)'}`,
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: '12px',
      fontWeight: 500,
      color: disabled ? '#bdbdbd' : isDark ? '#5fe0ad' : '#004744',
    }}
  >
    {label}
  </Box>
)

const AreasAndZonesTableGrid: FC<AreasAndZonesTableGridProps> = ({
  rows,
  interfaceId,
  loading,
  onEditItem: _onEditItem, // Kept for interface compatibility, not used
  onAddSubItem: _onAddSubItem, // Kept for interface compatibility, not used
  onDeleteItem,
  bulkEditMode = false,
  onRefetch,
  onSaveChanges,
  actionsRef,
}) => {
  const { t } = useTranslation()
  const { isViewOnly } = usePermission()
  // SCRUM-1032: inside the wizard the tree writes itself once the user pauses,
  // the same as the decoder, pipe and plant lists. Not on /config, where Save
  // is the contract.
  const isGuidedSetup = useIsGuidedSetup()
  const isDark = useSelector(selectTheme) === mode_type.dark
  const gridApiRef = useRef<GridApi<TreeDataRow> | null>(null)

  // Name of a newly created row, to scroll to once the refetch lands.
  const scrollToNameRef = useRef<string | null>(null)
  const [editedRows, setEditedRows] = useState<Set<number>>(new Set())
  const originalDataRef = useRef<TreeDataRow[]>([])
  const editModeRef = useRef(false)
  const [gridData, setGridData] = useState<TreeDataRow[]>([])
  const gridDataRef = useRef(gridData)
  gridDataRef.current = gridData

  // Staged creates, in the order they were added. They live outside `gridData`
  // so a refetch — which rebuilds the tree from the server rows — cannot drop
  // them; `rowData` splices them back into the tree for display.
  const [drafts, setDrafts] = useState<DraftRow[]>([])
  const draftsRef = useRef(drafts)
  draftsRef.current = drafts
  const draftIdRef = useRef(DRAFT_ID_START)

  // Rows a save refused to write, so the offending cell can be marked.
  const [invalidRowIds, setInvalidRowIds] = useState<Set<number>>(new Set())
  // Moves on every accepted edit, so the autosave debounce restarts on a burst
  // of typing. The names themselves live in a ref the effect cannot watch.
  const [editVersion, setEditVersion] = useState(0)

  const [addAreaRow, setAddAreaRow] = useState<TreeDataRow | null>(null)
  const addAreaRowRef = useRef(addAreaRow)
  addAreaRowRef.current = addAreaRow

  // Committed names, written synchronously on every edit. Save runs from a
  // toolbar button: the click blurs the open editor, which commits through
  // `handleCellValueChanged` in the same tick — the ref has the new value
  // before the click handler runs, where React state would still be stale.
  const namesRef = useRef<Map<number, string>>(new Map())

  // New areas whose sub-areas, staged while the create was out, still point at
  // the area's old draft id. Kept across saves: when the lookup that re-points
  // them fails, the next save tries again rather than stranding them for good.
  const createdParentsRef = useRef<Map<string, CreatedParent>>(new Map())

  useEffect(() => {
    const treeData: TreeDataRow[] = []

    // Preserve edited names across a refetch.
    const editedNameMap = new Map<number, string>()
    if (bulkEditMode && editedRows.size > 0) {
      gridDataRef.current.forEach((row) => {
        if (editedRows.has(row.id)) editedNameMap.set(row.id, row.name)
      })
    }

    const hasParent = (r: any) => !isTopLevelParentId(r?.parentId)

    const topAreas = rows.filter((r) => !hasParent(r))
    const subsByParent = new Map<string, AreaType[]>()
    rows.forEach((r) => {
      if (!hasParent(r)) return
      const key = String(r.parentId)
      if (!subsByParent.has(key)) subsByParent.set(key, [])
      subsByParent.get(key)!.push(r)
    })

    topAreas.forEach((area) => {
      const areaName = editedNameMap.get(area.id) ?? area.name
      // Id-suffixed path, so areas that share a name stay distinct.
      const areaPath = `${area.name}__${area.id}`
      treeData.push({
        id: area.id,
        interfaceId: area.interfaceId,
        name: areaName,
        parentId: area.parentId,
        level: area.level,
        isParent: true,
        path: [areaPath],
        kind: 'saved',
      })

      const seen = new Set<number>()
      const subAreas: AreaType[] = []
      for (const s of [
        ...(area.subAreas || []),
        ...(subsByParent.get(String(area.id)) || []),
      ]) {
        if (!seen.has(s.id)) {
          seen.add(s.id)
          subAreas.push(s)
        }
      }

      subAreas.forEach((subArea) => {
        treeData.push({
          id: subArea.id,
          interfaceId: subArea.interfaceId,
          name: editedNameMap.get(subArea.id) ?? subArea.name,
          parentId: String(area.id),
          level: (area.level ?? 0) + 1,
          isParent: false,
          parentName: area.name,
          path: [areaPath, `${subArea.name}__${subArea.id}`],
          kind: 'saved',
        })
      })

      // The per-area "+ Add Sub Area" row exists only in bulk edit. It stays put
      // once a draft has been added, so several sub-areas can be staged in a row.
      if (bulkEditMode) {
        treeData.push({
          id: addZoneChipId(area.id),
          interfaceId: area.interfaceId,
          name: CREATE_ZONE_PLACEHOLDER,
          parentId: String(area.id),
          level: (area.level ?? 0) + 1,
          isParent: false,
          parentName: area.name,
          path: [areaPath, `__CREATE_${area.id}__`],
          kind: 'addZoneChip',
        })
      }
    })

    setGridData(treeData)

    // Rebuilt from the server rows, then the staged drafts are laid back over
    // it: a refetch must not erase a name the user has typed but not saved.
    const nextNames = new Map(treeData.map((row) => [row.id, row.name]))
    draftsRef.current.forEach((draft) => nextNames.set(draft.id, draft.name))
    namesRef.current = nextNames

    // Falls back to the interfaceId prop so the first area can be added to an
    // empty list, where there is no existing row to read it from.
    const firstArea = rows[0]
    if (bulkEditMode) {
      setAddAreaRow({
        id: CREATE_AREA_ID,
        interfaceId: firstArea?.interfaceId ?? interfaceId,
        name: CREATE_AREA_PLACEHOLDER,
        parentId: '',
        level: 0,
        isParent: true,
        path: [CREATE_AREA_PLACEHOLDER],
        kind: 'addAreaChip',
      })
    } else {
      setAddAreaRow(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, bulkEditMode, interfaceId])

  // Scroll to a newly created row once the data has refreshed.
  useEffect(() => {
    if (!scrollToNameRef.current) return
    const targetName = scrollToNameRef.current
    scrollToNameRef.current = null
    const timeoutId = setTimeout(() => {
      const match = gridDataRef.current.find(
        (row) => row.name === targetName && row.id > 0,
      )
      if (match) gridApiRef.current?.scrollToRow(String(match.id))
    }, 200)
    return () => clearTimeout(timeoutId)
  }, [gridData])

  useEffect(() => {
    if (bulkEditMode && !editModeRef.current) {
      editModeRef.current = true
      originalDataRef.current = gridDataRef.current.map((row) => ({ ...row }))
    } else if (bulkEditMode) {
      // Rows that arrived since the snapshot — the ones a save just created —
      // join it with the name the server gave them. The snapshot is never
      // retaken while editing: retaking it after a refused rename absorbed the
      // correction typed since, so the next save found nothing to write and
      // reported "All changes saved" (SCRUM-1032 review, round 7).
      const known = new Set(originalDataRef.current.map((row) => row.id))
      gridDataRef.current.forEach((row) => {
        if (row.id > 0 && !known.has(row.id)) {
          originalDataRef.current.push({ ...row })
        }
      })
    } else if (!bulkEditMode && editModeRef.current) {
      editModeRef.current = false
      originalDataRef.current = []
      // The data is restored by handleCancelChanges, which refetches.
    }
  }, [bulkEditMode, gridData])

  useEffect(() => {
    if (bulkEditMode) return
    // Leaving bulk edit ends the staged batch, whichever way it ended.
    setDrafts([])
    setInvalidRowIds(new Set())
    draftIdRef.current = DRAFT_ID_START
  }, [bulkEditMode])

  // --- Editing --------------------------------------------------------------

  const handleCellValueChanged = useCallback(
    (event: { row: TreeDataRow; oldValue: unknown; newValue: unknown }) => {
      const { row } = event
      const next = String(event.newValue ?? '').trim()
      const previous = String(event.oldValue ?? '')

      // Typing into a staged draft stages the name — it is not written until
      // the toolbar Save runs, exactly like an edit to an existing row.
      if (row.kind === 'draft') {
        namesRef.current.set(row.id, next)
        setDrafts((prev) =>
          prev.map((draft) =>
            draft.id === row.id ? { ...draft, name: next } : draft,
          ),
        )
        if (next) {
          setInvalidRowIds((prev) => {
            if (!prev.has(row.id)) return prev
            const remaining = new Set(prev)
            remaining.delete(row.id)
            return remaining
          })
        }
        setEditVersion((version) => version + 1)
        return false as const
      }

      // Chip rows carry the "+ Add …" affordance and hold no value of their own.
      if (row.id < 0) return false as const

      // Clearing a name is not a delete, and an over-long one is rejected —
      // both put the old value back.
      if (!next) return false as const
      if (next.length > CONFIG_NAME_MAX_LENGTH) {
        toast.error(
          t(`Name must not exceed ${CONFIG_NAME_MAX_LENGTH} characters`),
        )
        return false as const
      }
      if (next === previous) return false as const

      namesRef.current.set(row.id, next)
      setGridData((prev) =>
        prev.map((candidate) =>
          candidate.id === row.id ? { ...candidate, name: next } : candidate,
        ),
      )
      setEditedRows((prev) => new Set(prev).add(row.id))
      setEditVersion((version) => version + 1)
      return false as const
    },
    [t],
  )

  const nameOf = useCallback(
    (row: TreeDataRow) =>
      (namesRef.current.get(row.id) ?? row.name ?? '').trim(),
    [],
  )

  // --- Staged drafts --------------------------------------------------------

  // The row is only in the DOM after the drafts state has rendered, so both the
  // scroll and the editor wait a tick.
  const startEditingRow = useCallback((rowId: number) => {
    setTimeout(() => {
      gridApiRef.current?.scrollToRow(String(rowId))
      gridApiRef.current?.startEditingCell(String(rowId), 'name')
    }, 0)
  }, [])

  const addDraft = useCallback(
    (draft: Omit<DraftRow, 'id' | 'name'>) => {
      gridApiRef.current?.stopEditing()
      const id = draftIdRef.current
      draftIdRef.current -= 1
      namesRef.current.set(id, '')
      setDrafts((prev) => [...prev, { ...draft, id, name: '' }])
      startEditingRow(id)
    },
    [startEditingRow],
  )

  const handleAddDraftArea = useCallback(() => {
    if (isViewOnly) return
    addDraft({
      interfaceId: addAreaRowRef.current?.interfaceId ?? interfaceId,
      parentId: '',
      isParent: true,
    })
  }, [addDraft, interfaceId, isViewOnly])

  const handleAddDraftZone = useCallback(
    (chipRow: TreeDataRow) => {
      if (isViewOnly) return
      // A collapsed area would hide the draft (and the editor) it just gained.
      gridApiRef.current?.setExpanded(chipRow.parentId, true)
      addDraft({
        interfaceId: chipRow.interfaceId,
        parentId: chipRow.parentId,
        isParent: false,
      })
    },
    [addDraft, isViewOnly],
  )

  /** Drops a single staged draft, leaving the rest of the batch alone. */
  // A new area takes the sub-areas staged under it along: they are not drawn
  // without it, and a blank one would hold the autosave off unseen.
  const handleDiscardDraft = useCallback((id: number) => {
    const { kept, removedIds } = discardDraftWithChildren(draftsRef.current, id)
    removedIds.forEach((removed) => namesRef.current.delete(removed))
    draftsRef.current = kept
    setDrafts(kept)
    setInvalidRowIds((prev) => {
      if (!removedIds.some((removed) => prev.has(removed))) return prev
      const remaining = new Set(prev)
      removedIds.forEach((removed) => remaining.delete(removed))
      return remaining
    })
  }, [])

  // Enter needs no handling of its own any more: the text editor commits on
  // Enter, which stages the name through `handleCellValueChanged` like any
  // other edit. Nothing is written until Save runs.

  // --- Bulk save / cancel ---------------------------------------------------

  const performSaveChanges = useCallback(async (options?: GuidedSaveOptions) => {
    // Commits the open editor before the staged rows are read. Not a
    // `sleep(0)`: a macrotask is throttled to one callback a minute in a
    // hidden tab, which parked the save (SCRUM-1032 QC, 6 Oct).
    //
    // From the autosave with an editor still open, a new row being typed is
    // skipped and its editor left alone: its create would re-key the row out
    // from under the editor (SCRUM-1032 QC, 30 Oct). Sub-areas of a skipped
    // new area wait with it, since they go up in its create. A saved row's
    // rename is not staged until its editor commits, so nothing of it is sent.
    const heldRowId = commitOrHoldOpenEditor(gridApiRef.current, options)

    const stagedDrafts = draftsRef.current
      .filter((draft) => String(draft.id) !== heldRowId)
      .map((draft) => ({
      ...draft,
      name: (namesRef.current.get(draft.id) ?? draft.name ?? '').trim(),
    }))

    // Every staged row is validated before anything is written: a half-applied
    // save would leave the user guessing which rows landed.
    const unnamed = stagedDrafts.filter((draft) => !draft.name)
    const tooLong = stagedDrafts.filter(
      (draft) => draft.name.length > CONFIG_NAME_MAX_LENGTH,
    )
    const invalid = [...unnamed, ...tooLong]
    if (invalid.length > 0) {
      setInvalidRowIds(new Set(invalid.map((draft) => draft.id)))
      toast.error(
        unnamed.length > 0
          ? t(
              unnamed.some((draft) => draft.isParent)
                ? 'formsValidationAreaNameIsRequired'
                : 'formsValidationSubAreaNameIsRequired',
            )
          : t(`Name must not exceed ${CONFIG_NAME_MAX_LENGTH} characters`),
      )
      // A non-zero `failed` keeps the page in bulk edit, so the batch survives.
      return { failed: invalid.length, total: stagedDrafts.length }
    }
    setInvalidRowIds(new Set())

    // Sub-areas staged under an area that is itself still a draft: the parent
    // has no server id to point at yet, so both go up in the area's own create
    // — `POST /api/area/parent` takes the sub-area names inline. Ordering the
    // calls and threading the new id back would be the alternative, and it
    // would leave the sub-areas orphaned if the parent create failed.
    const childrenByDraftParent = new Map<string, typeof stagedDrafts>()
    stagedDrafts.forEach((draft) => {
      if (draft.isParent || !isDraftParentId(draft.parentId)) return
      const list = childrenByDraftParent.get(draft.parentId)
      if (list) list.push(draft)
      else childrenByDraftParent.set(draft.parentId, [draft])
    })

    // Thunks, not promises: firing every request up front sent the whole batch
    // in parallel, and this API returns 500 under concurrent writes. A unit is
    // one request plus the drafts it covers, so a failure re-stages the whole
    // group — a parent and its children stand or fall together.
    const createUnits: Array<{
      draftIds: number[]
      names: string[]
      run: () => Promise<any>
    }> = []

    stagedDrafts.forEach((draft) => {
      if (!draft.isParent) return
      const children = childrenByDraftParent.get(String(draft.id)) ?? []
      createUnits.push({
        draftIds: [draft.id, ...children.map((child) => child.id)],
        names: [draft.name, ...children.map((child) => child.name)],
        run: () =>
          areaApi.create({
            interfaceId: draft.interfaceId,
            name: draft.name,
            subAreas: children.map((child) => child.name),
          }),
      })
    })

    stagedDrafts.forEach((draft) => {
      // The children handled above ride along with their parent's create.
      if (draft.isParent || isDraftParentId(draft.parentId)) return
      createUnits.push({
        draftIds: [draft.id],
        names: [draft.name],
        run: () =>
          areaApi.createSubArea({
            interfaceId: draft.interfaceId,
            parentId: draft.parentId,
            name: draft.name,
          }),
      })
    })

    const createTasks = createUnits.map((unit) => unit.run)

    // The areas that exist before this save's creates, so a new area can be
    // told apart from an older one with the same name.
    const areaIdsBefore = new Set(
      gridDataRef.current
        .filter((row) => row.kind === 'saved' && row.isParent)
        .map((row) => row.id),
    )

    const changedRows: TreeDataRow[] = []
    const changedAreaIds = new Set<number>()
    // PUT /api/area/parent replaces an area's sub-area list wholesale; an empty
    // subAreas array makes the backend reject the update, so collect the
    // current (possibly edited) sub-areas per area.
    const subAreasByParentId = new Map<number, { id: number; name: string }[]>()

    gridDataRef.current.forEach((row) => {
      if (row.id < 0) return
      const name = nameOf(row)

      if (!row.isParent) {
        const parentNumId = Number(row.parentId)
        if (!subAreasByParentId.has(parentNumId)) {
          subAreasByParentId.set(parentNumId, [])
        }
        subAreasByParentId.get(parentNumId)!.push({ id: row.id, name })
      }

      const original = originalDataRef.current.find(
        (item) => item.id === row.id,
      )
      if (original && original.name !== name) {
        changedRows.push({ ...row, name })
        if (row.isParent) changedAreaIds.add(row.id)
      }
    })

    // A row renamed back to its original name has nothing left to save: it
    // stops counting as edited, rather than staying amber for good.
    const changedIds = new Set(changedRows.map((row) => row.id))
    setEditedRows((previous) => {
      const next = new Set(
        [...previous].filter((id) => id < 0 || changedIds.has(id)),
      )
      return next.size === previous.size ? previous : next
    })

    // Skip sub-area updates whose parent area is also being updated — the
    // updateArea call already carries the new sub-area name.
    const renameRows = changedRows.filter(
      (row) => row.isParent || !changedAreaIds.has(Number(row.parentId)),
    )
    const renameTasks = renameRows.map(
        (row) => () =>
          row.isParent
            ? areaApi.updateArea({
                id: row.id,
                interfaceId: row.interfaceId,
                name: row.name,
                subAreas: subAreasByParentId.get(row.id) ?? [],
              })
            : areaApi.updateSubArea({
                id: row.id,
                interfaceId: row.interfaceId,
                parentId: row.parentId,
                name: row.name,
              }),
      )

    // Renames first: `updateArea` replaces an area's sub-area list wholesale
    // with the sub-areas collected above, which a staged draft is not yet part
    // of. Creating first would let the rename that follows drop the new row.
    const tasks = [...renameTasks, ...createTasks]

    if (tasks.length === 0) {
      if (onSaveChanges) onSaveChanges()
      return { failed: 0, total: 0 }
    }

    // One at a time, with a retry: the API returns 500 when area writes arrive
    // concurrently, which silently dropped most of a large batch.
    let failed = 0
    let lastError: any = null
    const failedTaskIndexes = new Set<number>()

    for (const [index, task] of tasks.entries()) {
      let saved = false

      for (let attempt = 0; attempt < SAVE_RETRY_LIMIT && !saved; attempt++) {
        try {
          await task()
          saved = true
        } catch (error: any) {
          lastError = error
          // A 4xx is the server refusing the value: resending cannot help.
          if (!isRetryableApiError(error)) break
          if (attempt < SAVE_RETRY_LIMIT - 1) await sleep(SAVE_RETRY_DELAY_MS)
        }
      }

      if (!saved) {
        failed += 1
        failedTaskIndexes.add(index)
      }
      await sleep(SAVE_REQUEST_GAP_MS)
    }

    // The creates trail the renames in `tasks`, so unit N is task N + offset.
    // Only the drafts that did not land stay staged: re-staging the ones that
    // succeeded would create them a second time on the next Save.
    const createOffset = renameTasks.length
    const failedDraftIds = new Set<number>()
    const createdNames: string[] = []
    createUnits.forEach((unit, index) => {
      if (failedTaskIndexes.has(index + createOffset)) {
        unit.draftIds.forEach((id) => failedDraftIds.add(id))
      } else {
        createdNames.push(...unit.names)
      }
    })
    const createdDraftIds = stagedDrafts
      .map((draft) => draft.id)
      .filter((id) => !failedDraftIds.has(id))

    // A sub-area added under a NEW area while that area's create was out was
    // not in its create, and is now staged under a draft id that no longer
    // exists — it fell out of the tree and was never sent (SCRUM-1032 round 7,
    // D2 check). It is pointed at the area that was created, found among the
    // areas that did not exist before (see resolveCreatedParents).
    stagedDrafts.forEach((draft) => {
      if (draft.isParent && !failedDraftIds.has(draft.id)) {
        createdParentsRef.current.set(String(draft.id), {
          name: draft.name,
          before: areaIdsBefore,
        })
      }
    })
    const pointsAtCreatedParent = (draft: DraftRow) =>
      !draft.isParent && createdParentsRef.current.has(String(draft.parentId))
    let resolvedParents = new Map<string, number>()
    if (draftsRef.current.some(pointsAtCreatedParent)) {
      try {
        const { data } = await areaApi.getAll({
          interfaceId: Number(interfaceId),
          PageIndex: 1,
          PageSize: 0,
        } as any)
        resolvedParents = resolveCreatedParents(
          createdParentsRef.current,
          (data as any)?.data ?? [],
        )
      } catch {
        // Left staged as it was; the next save tries the lookup again.
      }
    }

    // From the CURRENT drafts, read after every await: a row added while the
    // requests were out is not in `stagedDrafts` and must survive
    // (SCRUM-1032 review, round 7).
    const remainingDrafts = retireCreatedDrafts(
      draftsRef.current,
      createdDraftIds,
    ).map((draft) => {
      if (draft.isParent) return draft
      const parentId = resolvedParents.get(String(draft.parentId))
      return parentId === undefined
        ? draft
        : { ...draft, parentId: String(parentId) }
    })
    // Forget a created area once nothing staged points at it any more.
    Array.from(createdParentsRef.current.keys()).forEach((draftId) => {
      if (!remainingDrafts.some((draft) => draft.parentId === draftId)) {
        createdParentsRef.current.delete(draftId)
      }
    })

    // The renames that landed. A sub-area folded into its parent's update
    // landed when that update did.
    const landedRenames = changedRows.filter((row) => {
      const own = renameRows.indexOf(row)
      if (own >= 0) return !failedTaskIndexes.has(own)
      const parent = renameRows.findIndex(
        (candidate) =>
          candidate.isParent && candidate.id === Number(row.parentId),
      )
      return parent >= 0 && !failedTaskIndexes.has(parent)
    })
    if (createdNames.length > 0) {
      scrollToNameRef.current = createdNames[createdNames.length - 1]
    }

    if (failed === 0) {
      // SCRUM-1032: inside the wizard the step shows one status for every
      // grid on it, so six chips added one at a time no longer raise six
      // toasts. On /config, Save is a deliberate act and still confirms.
      if (!isGuidedSetup) toast.success(t('updateSuccess'))
    } else if (failed === tasks.length) {
      // The server's reason, under one id so a repeat replaces it.
      toast.error(getApiErrorMessage(lastError, t('somethingWentWrong')), {
        id: 'areas-save-error',
      })
    } else {
      toast.error(t('bulkSavePartialError', { failed, total: tasks.length }), {
        id: 'areas-save-error',
      })
    }

    // Only what landed is settled. The snapshot moves to the name that was
    // written, and the row stops counting as edited — unless it was renamed
    // again while the request was out, in which case the newer name is still
    // to be saved. A refused rename keeps both: it is sent again, and keeps
    // counting as failed, until it lands or is undone.
    landedRenames.forEach((row) => {
      const original = originalDataRef.current.find(
        (item) => item.id === row.id,
      )
      if (original) original.name = row.name
    })
    setEditedRows((previous) => {
      const next = new Set(previous)
      landedRenames.forEach((row) => {
        const current = gridDataRef.current.find(
          (candidate) => candidate.id === row.id,
        )
        if (!current || nameOf(current) === row.name) next.delete(row.id)
      })
      return next
    })
    setInvalidRowIds(new Set())
    // Written through the ref as well as the state: the refetch below rebuilds
    // the tree, and that effect reads `draftsRef` to keep unsaved names.
    draftsRef.current = remainingDrafts
    setDrafts(remainingDrafts)

    if (onRefetch) await onRefetch()
    if (onSaveChanges && failed === 0) onSaveChanges()

    return { failed, total: tasks.length }
  }, [nameOf, onRefetch, onSaveChanges, t, interfaceId, isGuidedSetup])

  // SCRUM-1032 QC items 12 / 15b — one save at a time. The pause autosave
  // and the wizard's Next flush both call this, and two overlapping runs
  // read the same staged rows and write them twice.
  const { save: handleSaveChanges, isSaving } =
    useSerialisedSave(performSaveChanges)

  const handleCancelChanges = useCallback(() => {
    setEditedRows(new Set())
    setInvalidRowIds(new Set())
    draftsRef.current = []
    setDrafts([])
    createdParentsRef.current = new Map()
    draftIdRef.current = DRAFT_ID_START
    if (onRefetch) onRefetch()
  }, [onRefetch])


  // A row still missing its name cannot be written — `handleSaveChanges`
  // rejects the whole batch and marks the cell — so autosave waits rather than
  // raising the same complaint every few seconds while it is being typed. The
  // name lives in `namesRef` until the save reads it, so that is what is
  // checked, not the draft's own stale copy.
  const draftsReadyToSave = drafts.every((draft) =>
    (namesRef.current.get(draft.id) ?? draft.name ?? '').trim(),
  )

  useGuidedAutosave({
    enabled: isGuidedSetup && bulkEditMode && !isViewOnly && draftsReadyToSave,
    hasPendingChanges: drafts.length > 0 || editedRows.size > 0,
    version: editVersion,
    save: handleSaveChanges,
    keepsEditorOpen: true,
    // An open editor is the user mid-value; the save would close it.
    isEditing: () => gridApiRef.current?.isEditingCell() ?? false,
    isBusy: isSaving,
  })

  useEffect(() => {
    if (!actionsRef) return
    actionsRef.current = {
      save: handleSaveChanges,
      cancel: handleCancelChanges,
    }
    return () => {
      actionsRef.current = null
    }
  }, [actionsRef, handleSaveChanges, handleCancelChanges])

  // --- Renderers ------------------------------------------------------------

  const ActionsCellRenderer = useCallback(
    ({ row }: GridCellCtx<TreeDataRow>) => {
      if (!row) return null

      // A chip row is an affordance, not a record — nothing to act on.
      if (row.kind === 'addAreaChip' || row.kind === 'addZoneChip') return null
      if (!bulkEditMode) return null

      // A draft gets the same ✕ as a saved row, so one staged item can be
      // dropped without cancelling the whole batch. It has never been written,
      // so it is discarded locally instead of going through the delete modal.
      const onClick =
        row.kind === 'draft'
          ? () => handleDiscardDraft(row.id)
          : () => {
              if (isViewOnly) return
              onDeleteItem(
                row.id,
                String(row.interfaceId),
                row.isParent ? 'area' : 'subarea',
                row.name,
              )
            }

      return (
        <Box data-no-cell-edit sx={ROW_ACTIONS_SX}>
          <DeleteIconButton
            disabled={isViewOnly}
            isDark={isDark}
            onClick={onClick}
          />
        </Box>
      )
    },
    [bulkEditMode, isViewOnly, isDark, onDeleteItem, handleDiscardDraft],
  )

  const NameCellRenderer = useCallback(
    ({ row, value }: GridCellCtx<TreeDataRow>) => {
      if (!row) return null

      // The chips stay put once a draft has been added, so several items can be
      // staged one after another.
      if (row.kind === 'addAreaChip') {
        return (
          <AddRowChip
            label={`+ ${t('actionAddArea')}`}
            disabled={isViewOnly}
            isDark={isDark}
            onClick={handleAddDraftArea}
          />
        )
      }

      if (row.kind === 'addZoneChip') {
        return (
          <AddRowChip
            label={`+ ${t('actionAddSubArea')}`}
            disabled={isViewOnly}
            isDark={isDark}
            onClick={() => handleAddDraftZone(row)}
          />
        )
      }

      return <span>{value as string}</span>
    },
    [t, isViewOnly, isDark, handleAddDraftArea, handleAddDraftZone],
  )

  // --- Columns --------------------------------------------------------------

  const nameColumn = useMemo<GridColumn<TreeDataRow>>(
    () => ({
      field: 'name',
      headerName: t('AreasTableHeaderAreas'),
      minWidth: 200,
      flex: 1,
      editor: 'text',
      editorParams: { maxLength: CONFIG_NAME_MAX_LENGTH },
      editable: ({ row }) => {
        if (row.kind === 'addAreaChip' || row.kind === 'addZoneChip')
          return false
        if (row.kind === 'draft') return !isViewOnly
        return bulkEditMode && !isViewOnly
      },
      cellRenderer: NameCellRenderer,
      cellStyle: ({ row }) => ({
        // Top-level areas are bold so each area's root stands out from its
        // sub-areas (matches the Hydraulic mainlines). The chip is not a record,
        // so it stays regular weight.
        fontWeight: row.isParent && row.kind !== 'addAreaChip' ? 700 : 400,
      }),
      cellClass: ({ row }) => {
        const list: string[] = []
        // A draft takes the whole-row `new` tint instead of the per-cell
        // editable ring, so the two signals stay distinguishable.
        if (bulkEditMode && !isViewOnly && row.kind === 'saved') {
          list.push(gridCellClasses.editable)
        }
        if (editedRows.has(row.id)) list.push(gridCellClasses.changed)
        if (invalidRowIds.has(row.id)) list.push(gridCellClasses.invalid)
        return list
      },
    }),
    [t, bulkEditMode, isViewOnly, editedRows, invalidRowIds, NameCellRenderer],
  )

  const columns = useMemo<GridColumn<TreeDataRow>[]>(() => {
    const actionsColumn: GridColumn<TreeDataRow> = {
      field: 'actions',
      // No header label — the actions are icon buttons, and an empty header
      // reads cleaner beside the single-header simple lists.
      headerName: '',
      // One icon button. Flexing it gave the column half the panel — 209px of
      // mostly empty space — which was width the Emitter list next door
      // needed for its Efficiency column.
      width: 52,
      sortable: false,
      editable: false,
      cellRenderer: ActionsCellRenderer,
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      cellClass: () => (bulkEditMode ? gridCellClasses.nonEditable : ''),
    }

    return [actionsColumn]
  }, [bulkEditMode, ActionsCellRenderer])

  // Drafts are spliced into the tree here rather than held in `gridData`, so a
  // refetch can rebuild the server rows underneath them without losing any.
  // A new sub-area lands directly above its area's "+ Add Sub Area" chip; a new
  // area goes after the saved areas, above the "+ Add Area" chip.
  const rowData = useMemo(() => {
    if (drafts.length === 0) {
      return addAreaRow ? [...gridData, addAreaRow] : gridData
    }

    const zoneDraftsByParent = new Map<string, DraftRow[]>()
    drafts.forEach((draft) => {
      if (draft.isParent) return
      const list = zoneDraftsByParent.get(draft.parentId)
      if (list) list.push(draft)
      else zoneDraftsByParent.set(draft.parentId, [draft])
    })

    const merged: TreeDataRow[] = []

    gridData.forEach((row) => {
      if (row.kind === 'addZoneChip') {
        ;(zoneDraftsByParent.get(row.parentId) ?? []).forEach((draft) => {
          merged.push({
            id: draft.id,
            interfaceId: draft.interfaceId,
            name: draft.name,
            parentId: draft.parentId,
            level: row.level,
            isParent: false,
            parentName: row.parentName,
            path: [row.path[0], `__DRAFT_${draft.id}__`],
            kind: 'draft',
          })
        })
      }
      merged.push(row)
    })

    // A staged area carries its own sub-area drafts and its own "+ Add Sub
    // Area" chip, so sub-areas can be filled in before the area is ever saved.
    drafts.forEach((draft) => {
      if (!draft.isParent) return
      const areaPath = `__DRAFT_${draft.id}__`
      merged.push({
        id: draft.id,
        interfaceId: draft.interfaceId,
        name: draft.name,
        parentId: '',
        level: 0,
        isParent: true,
        path: [areaPath],
        kind: 'draft',
      })

      ;(zoneDraftsByParent.get(String(draft.id)) ?? []).forEach((child) => {
        merged.push({
          id: child.id,
          interfaceId: child.interfaceId,
          name: child.name,
          parentId: child.parentId,
          level: 1,
          isParent: false,
          parentName: draft.name,
          path: [areaPath, `__DRAFT_${child.id}__`],
          kind: 'draft',
        })
      })

      merged.push({
        id: addZoneChipId(Math.abs(draft.id)),
        interfaceId: draft.interfaceId,
        name: CREATE_ZONE_PLACEHOLDER,
        parentId: String(draft.id),
        level: 1,
        isParent: false,
        parentName: draft.name,
        path: [areaPath, `__CREATE_${draft.id}__`],
        kind: 'addZoneChip',
      })
    })

    if (addAreaRow) merged.push(addAreaRow)
    return merged
  }, [gridData, addAreaRow, drafts])

  const rowClass = useCallback(
    ({ row }: { row: TreeDataRow }) =>
      row.kind === 'draft' ? gridRowClasses.new : undefined,
    [],
  )

  const getRowId = useCallback((row: TreeDataRow) => String(row.id), [])
  const getPath = useCallback((row: TreeDataRow) => row.path, [])

  // Memoised: the grid rebuilds its whole row model whenever this object
  // changes, so an inline literal would re-flatten the tree on every render.
  const treeData = useMemo(
    () => ({ getPath, groupColumn: nameColumn, defaultExpanded: true }),
    [getPath, nameColumn],
  )

  return (
    <DataGrid<TreeDataRow>
      columns={columns}
      rows={rowData}
      loading={loading}
      getRowId={getRowId}
      apiRef={gridApiRef}
      rowHeight={40}
      headerHeight={42}
      editMode="singleClick"
      treeData={treeData}
      rowClass={rowClass}
      onCellValueChanged={handleCellValueChanged}
    />
  )
}

export default AreasAndZonesTableGrid
